import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import ProductsHeader from './containers/ProductsHeader';
import Home from './pages/home';
import Omnichannel from './pages/omnichannel';
import Contact from './containers/contactContainer';
import { Overlay } from './pages/home/style';
import Products from './pages/products';

const IDLE_TIMEOUT_MS = 2000;
const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
let analyticsInitialized = false;

const getPrimaryMeasurementId = () => {
  const rawValue = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
  const ids = rawValue
    .split(/[,\s;|]+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value) => value.startsWith('G-'));

  return ids[0] || null;
};

const linkOptions = {
  threshold: 0.9,
};

function App() {
  const [modal, setModal] = useState(false);
  const isHomePage = window.location.pathname === '/';
  const isIndexHtml = window.location.href.includes('index.html');
  const gaMeasurementId = getPrimaryMeasurementId();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !gaMeasurementId || analyticsInitialized) {
      return undefined;
    }

    let timeoutId;
    let idleCallbackId;

    const initializeAnalytics = () => {
      if (analyticsInitialized) return;

      analyticsInitialized = true;
      ReactGA.initialize(gaMeasurementId);
      ReactGA.send({
        hitType: 'pageview',
        page: `${window.location.pathname}${window.location.search}`,
      });
      INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, initializeAnalytics);
      });
    };

    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(initializeAnalytics, {
        timeout: IDLE_TIMEOUT_MS,
      });
    } else {
      timeoutId = window.setTimeout(initializeAnalytics, IDLE_TIMEOUT_MS);
    }

    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, initializeAnalytics, {
        passive: true,
      });
    });

    return () => {
      INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, initializeAnalytics);
      });
      if (idleCallbackId) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [gaMeasurementId]);

  const openModal = (open) => {
    if (open) {
      setModal(open);
      window.modalActive = true;
    }
  };

  const closeModal = () => {
    window.modalActive = false;
    setModal('');
  };

  const { ref: OmnichannelFooterRef, inView: OmnichannelFooter } = useInView(linkOptions);
  const { ref: PaymentRef, inView: PaymentOptions } = useInView(linkOptions);

  return (
    <Router>
      <Overlay onClick={() => closeModal()} open={modal === 'contact'} />
      {isIndexHtml && <Navigate to="/" replace />}
      {!isHomePage && (
        <ProductsHeader
          showLinks={!isHomePage}
          darkMode={OmnichannelFooter || PaymentOptions}
          handleOpen={(modalName) => openModal(modalName)}
          handleClose={() => closeModal()}
        />
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/i9.Omnichannel"
          element={<Omnichannel footerRef={OmnichannelFooterRef} />}
        />
        <Route
          path="/i9.Platform"
          element={
            (
              <Products
                footerRef={OmnichannelFooterRef}
                paymentRef={PaymentRef}
                handleOpen={(modalName) => openModal(modalName)}
              />
            )
          }
        />
      </Routes>
      <Contact open={modal === 'contact'} handleClose={closeModal} />
    </Router>
  );
}

export default App;
